// Today view + Dashboard + Reflection
import React from 'react';
import { Icon, Pyramid } from './icons.jsx';
import { TODAY, TOP3 } from './data.js';

const TodayHeader = ({ progress }) => {
  const today = new Date();
  const day = today.toLocaleDateString("en-US", { weekday: "long" });
  const date = today.getDate();
  const month = today.toLocaleDateString("en-US", { month: "long" });
  return (
    <div className="page__head">
      <div>
        <h1 className="page__title">{day}, <em>{date}</em> {month}</h1>
        <div className="page__sub">
          <span className="mono">Cycle 9 · 16</span>
          <span style={{ margin: "0 10px", color: "var(--ink-5)" }}>·</span>
          <span><strong style={{ color: "var(--ink-1)" }}>{progress.done}</strong> of <strong style={{ color: "var(--ink-1)" }}>{progress.scheduled}</strong> cycles complete</span>
          <span style={{ margin: "0 10px", color: "var(--ink-5)" }}>·</span>
          <span>Top 3 — <span style={{ color: "var(--success)" }}>1</span> of 3 done</span>
        </div>
      </div>
      <div className="row" style={{ gap: 10 }}>
        <button className="btn btn--outline btn--sm">
          <Icon name="refresh" size={13} />
          <span>All deliverables</span>
        </button>
        <button className="btn btn--accent">
          <Icon name="sparkles" size={14} />
          <span>Fill the day</span>
        </button>
      </div>
    </div>
  );
};

const StampRibbon = () => (
  <div className="row" style={{ gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
    <span className="mono" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-3)" }}>Day stamps</span>
    <span className="chip" style={{ background: "oklch(95% 0.04 290)", color: "oklch(40% 0.10 290)", borderColor: "transparent" }}>
      <span className="dot" style={{ background: "oklch(58% 0.10 290)" }}></span>Deep work day
    </span>
    <span className="chip" style={{ background: "oklch(95% 0.04 220)", color: "oklch(40% 0.10 220)", borderColor: "transparent" }}>
      <span className="dot" style={{ background: "oklch(60% 0.15 220)" }}></span>Family Friday
    </span>
    <span className="chip" style={{ borderStyle: "dashed", color: "var(--ink-3)" }}>
      <Icon name="plus" size={11} />
      Add stamp
    </span>
  </div>
);

const Top3Card = ({ items, onToggle }) => (
  <div className="card" style={{ marginBottom: 24 }}>
    <div className="card__head">
      <Icon name="target" size={15} />
      <div className="card__title">Today's <em>Top 3</em></div>
      <span className="card__sub" style={{ marginLeft: "auto" }}>{items.filter(i => i.done).length} / 3 done</span>
    </div>
    <div className="card__body" style={{ padding: 0 }}>
      {items.map((t, idx) => (
        <div key={t.id} className="row" style={{
          padding: "14px 24px",
          borderTop: idx === 0 ? 0 : "1px solid var(--hair)",
          gap: 14
        }}>
          <button className="tick" data-checked={t.done ? "1" : "0"} onClick={() => onToggle(t.id)}>
            <Icon name="check" size={12} stroke={2.4} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em",
              color: t.done ? "var(--ink-3)" : "var(--ink-1)",
              textDecoration: t.done ? "line-through" : "none",
              transition: "color var(--t)"
            }}>{t.title}</div>
            <div className="row" style={{ gap: 8, marginTop: 4 }}>
              <span className="pdot" style={{ background: t.color, width: 8, height: 8 }}></span>
              <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{t.project}</span>
              <span className="mono" style={{ fontSize: 11, color: "var(--ink-4)" }}>· {t.cycles} cycle</span>
            </div>
          </div>
          <button className="btn btn--ghost btn--sm">
            <Icon name="calendar" size={12} /><span>Schedule</span>
          </button>
        </div>
      ))}
    </div>
  </div>
);

const QuickCapture = () => {
  const [val, setVal] = React.useState("");
  return (
    <div className="card" style={{ marginBottom: 24, padding: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{
        width: 32, height: 32, flex: "none",
        background: "var(--accent-soft)", color: "var(--accent)",
        borderRadius: 8, display: "grid", placeItems: "center"
      }}>
        <Icon name="brain" size={16} />
      </div>
      <textarea
        className="textarea"
        placeholder="Brain dump — anything on your mind. Convert later."
        rows={1}
        value={val}
        onChange={e => setVal(e.target.value)}
        style={{ minHeight: 32, padding: "6px 0", border: 0, background: "transparent", boxShadow: "none", resize: "none" }}
      />
      <div className="row" style={{ gap: 6, alignSelf: "flex-end" }}>
        <button className="iconbtn" title="Voice"><Icon name="mic" size={15} /></button>
        <button className="btn btn--primary btn--sm" disabled={!val}>Capture</button>
      </div>
    </div>
  );
};

const CycleRow = ({ c, onComplete, collapsing }) => {
  if (c.state === "blockcont") {
    return (
      <div className="cycle" style={{ background: "var(--surface)" }}>
        <div className="cycle__num"><strong>{String(c.i).padStart(2, "0")}</strong></div>
        <div className="cycle__time">{c.t}</div>
        <div className="cycle__body">
          <div className="cycle__stripe" style={{ background: c.color, opacity: 0.55 }}></div>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-4)" }}>↳ continued</span>
        </div>
        <div></div>
      </div>
    );
  }
  if (c.state === "sleep") {
    return (
      <div className="cycle cycle--sleep">
        <div className="cycle__num"><strong>{String(c.i).padStart(2, "0")}</strong></div>
        <div className="cycle__time">{c.t}</div>
        <div className="cycle__body" style={{ color: "var(--ink-4)" }}>
          <Icon name="moon" size={13} />
          <span className="cycle__title">Sleep</span>
        </div>
        <div></div>
      </div>
    );
  }
  if (c.state === "empty") {
    return (
      <div className="cycle cycle--empty">
        <div className="cycle__num"><strong>{String(c.i).padStart(2, "0")}</strong></div>
        <div className="cycle__time">{c.t}</div>
        <div className="cycle__body"></div>
        <div></div>
      </div>
    );
  }
  const isCurrent = c.state === "current";
  const isDone = c.done;
  return (
    <div className={
      "cycle" +
      (isCurrent ? " cycle--current" : "") +
      (isDone ? " cycle--done" : "") +
      (collapsing ? " cycle--collapsing" : "")
    } style={c.span ? { paddingBottom: 12 + (c.span - 1) * 0 } : null}>
      <div className="cycle__num"><strong>{String(c.i).padStart(2, "0")}</strong>{c.span && c.span > 1 && <span style={{ marginLeft: 4, opacity: 0.6 }}>·{c.span}</span>}</div>
      <div className="cycle__time">{c.t}</div>
      <div className="cycle__body">
        <div className="cycle__stripe" style={{ background: c.color }}></div>
        {c.icon ? (
          <div style={{
            width: 22, height: 22, flex: "none",
            borderRadius: 6, display: "grid", placeItems: "center",
            background: c.color, color: "white"
          }}>
            <Icon name={c.icon} size={12} stroke={1.8} />
          </div>
        ) : null}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="cycle__title">
            {c.title}
            {isCurrent && (
              <span style={{ marginLeft: 10, fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--accent)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                ● now
              </span>
            )}
          </div>
          <div className="cycle__meta">{c.project}{c.task ? ` · ${c.task}` : ""}</div>
        </div>
      </div>
      <div className="row" style={{ gap: 4 }}>
        {!isDone && c.state !== "block" && c.state !== "current" && (
          <button className="cycle__actions iconbtn"><Icon name="more" size={14} /></button>
        )}
        {isCurrent && (
          <button className="btn btn--accent btn--sm">
            <Icon name="play" size={11} /><span>Start</span>
          </button>
        )}
        <button
          className="tick"
          data-checked={isDone ? "1" : "0"}
          onClick={() => onComplete(c.i)}
          title={isDone ? "Completed" : "Mark complete"}
        >
          <Icon name="check" size={11} stroke={2.6} />
        </button>
      </div>
      {isCurrent && <div className="now-dot"></div>}
    </div>
  );
};

const TodayView = () => {
  const [cycles, setCycles] = React.useState(TODAY);
  const [top3, setTop3] = React.useState(TOP3);
  const [collapsing, setCollapsing] = React.useState(null);
  const [doneStackOpen, setDoneStackOpen] = React.useState(true);

  const onComplete = (i) => {
    setCollapsing(i);
    setTimeout(() => {
      setCycles(cs => cs.map(c => c.i === i ? { ...c, done: !c.done } : c));
      setCollapsing(null);
    }, 380);
  };

  const onToggleTop = (id) => {
    setTop3(t => t.map(x => x.id === id ? { ...x, done: !x.done } : x));
  };

  const active = cycles.filter(c => c.state !== "sleep" && c.state !== "blockcont" && !c.done);
  const done = cycles.filter(c => c.done);
  const sleepRows = cycles.filter(c => c.state === "sleep");
  const sched = cycles.filter(c => c.state !== "empty" && c.state !== "sleep" && c.state !== "blockcont").length;

  return (
    <div className="page">
      <TodayHeader progress={{ done: done.length, scheduled: sched }} />
      <StampRibbon />
      <QuickCapture />
      <Top3Card items={top3} onToggle={onToggleTop} />

      <div className="row" style={{ marginBottom: 12 }}>
        <h3 style={{ fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em", color: "var(--ink-1)", margin: 0 }}>
          The day
        </h3>
        <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
          {sched - done.length} active · {done.length} done · {sleepRows.length} sleep
        </span>
        <button className="btn btn--ghost btn--sm" style={{ marginLeft: "auto" }}>
          <Icon name="plus" size={13} /><span>Schedule</span>
        </button>
      </div>

      <div className="cycles">
        {active.map(c => (
          <CycleRow key={c.i} c={c} onComplete={onComplete} collapsing={collapsing === c.i} />
        ))}
      </div>

      {/* Done stack */}
      <div className="card" style={{ marginTop: 24, background: "var(--surface-2)" }}>
        <button
          className="card__head"
          onClick={() => setDoneStackOpen(o => !o)}
          style={{ width: "100%", borderBottom: doneStackOpen ? "1px solid var(--hair)" : "0", background: "transparent", borderTopLeftRadius: 10, borderTopRightRadius: 10 }}
        >
          <div style={{
            width: 24, height: 24, borderRadius: 6,
            background: "var(--success-soft)", color: "var(--success)",
            display: "grid", placeItems: "center"
          }}>
            <Icon name="check" size={12} stroke={2.6} />
          </div>
          <div className="card__title">Done today</div>
          <span className="chip chip--success">{done.length}</span>
          <Icon name="chev_down" size={14} style={{ marginLeft: "auto", transition: "transform var(--t-fast)", transform: doneStackOpen ? "rotate(0)" : "rotate(-90deg)" }} />
        </button>
        {doneStackOpen && (
          <div className="card__body" style={{ padding: 0 }}>
            {done.map((c, idx) => (
              <div key={c.i} className="row" style={{
                padding: "10px 24px",
                borderTop: idx === 0 ? 0 : "1px solid var(--hair)",
                gap: 14, opacity: 0.85
              }}>
                <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)", width: 30 }}>{String(c.i).padStart(2, "0")}</span>
                <span className="cycle__stripe" style={{ background: c.color, opacity: 0.5, height: 16, width: 3, borderRadius: 2 }}></span>
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", textDecoration: "line-through", flex: 1 }}>{c.title}</span>
                <span className="mono" style={{ fontSize: 10.5, color: "var(--ink-3)" }}>{c.project}</span>
                <span className="mono" style={{ fontSize: 10.5, color: "var(--success)" }}>✓ {c.t.split(" ")[2]}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sleep block */}
      <div style={{ marginTop: 16, padding: "12px 24px", border: "1px dashed var(--line)", borderRadius: 10, color: "var(--ink-3)", display: "flex", alignItems: "center", gap: 10, fontSize: 12.5 }}>
        <Icon name="moon" size={13} />
        <span>Sleep cycles 12 – 16 · 7.5 hours · 10:30 PM → 6:00 AM</span>
        <span className="mono" style={{ marginLeft: "auto", fontSize: 11 }}>16 / 16</span>
      </div>
    </div>
  );
};

export { TodayView };
